/**
 * Account menu — shows the logged-in user, a Sign out button, and a
 * Sign-out-everywhere button. Used on admin pages that don't render
 * the WorkbenchShell toolbar.
 *
 * "Sign out everywhere" calls /auth/logout-all, which bumps the user's
 * session_version on the server. Every JWT issued before that bump is
 * instantly invalidated — useful when you forgot to log out on a
 * friend's machine.
 */
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useLang } from '@/i18n/useLang';

export function AccountMenu() {
  const { user, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleLogoutAll = async () => {
    const ok = window.confirm(
      t(
        'सभी डिवाइस से लॉग आउट हो जाएगा। पुष्टि करें?',
        "This will sign you out from every device you've used. Continue?",
      ),
    );
    if (!ok) return;
    await logoutAll();
    navigate('/', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-2 py-1 text-stone-400">
        <span aria-hidden>👤</span>
        <span className="font-medium text-stone-200">{user.username}</span>
        <span className="rounded bg-saffron-700/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-saffron-200">
          {user.role}
        </span>
      </span>
      <button
        type="button"
        onClick={() => setLang(lang === 'hi' ? 'en' : 'hi')}
        className="rounded-md border border-saffron-600 bg-stone-950 px-2 py-1 font-medium text-saffron-200 hover:bg-saffron-700/30"
        aria-label={lang === 'hi' ? 'Switch to English' : 'हिन्दी में स्विच करें'}
        title={t('PDF भी इसी भाषा में आएगी', 'Affects PDF downloads too')}
      >
        {lang === 'hi' ? 'EN' : 'हिं'}
      </button>
      <button
        type="button"
        onClick={() => void handleLogout()}
        className="rounded-md border border-stone-700 px-2 py-1 text-stone-300 hover:border-rose-700 hover:text-rose-200"
      >
        {t('लॉग आउट', 'Sign out')}
      </button>
      <button
        type="button"
        onClick={() => void handleLogoutAll()}
        title={t(
          'किसी और डिवाइस पर खुला सेशन भी समाप्त कर देगा',
          'Revokes every device where you are currently signed in',
        )}
        className="rounded-md border border-rose-900 bg-rose-950/30 px-2 py-1 text-rose-200 hover:bg-rose-950/60"
      >
        {t('सभी जगह से लॉग आउट', 'Sign out everywhere')}
      </button>
    </div>
  );
}
